// src/features/analytics/AnalyticsUtils.ts

import { ApiError } from '@/shared/api/ApiError';
import { ANALYTICS_LIVE_STALE_AFTER_SECONDS } from '@/lib/constants/analytics';

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
// Binary base, matching the platform's existing byte convention
// (AccountUtils: 1 GB = 1024^3 bytes on /account/usage).
const BYTES_PER_UNIT = 1024;

export type AnalyticsErrorKind =
  | 'empty'           // no data for the range (range_outside_coverage / product window)
  | 'busy'            // transient overload — retry shortly
  | 'timeout'         // query took too long — narrow the range
  | 'rangeInvalid'    // bad range/selector (should be prevented by the controls)
  | 'notAvailable'    // scope errors — not available for this account
  | 'notAuthorized'   // platform VError 403
  | 'notEnabled'      // gateway flag off (plain 404, expected in local dev)
  | 'generic';

export interface AnalyticsErrorView {
  kind: AnalyticsErrorKind;
  title: string;
  detail?: string;
  availableFrom?: number;
  availableTo?: number;
}

/**
 * Formatting and error-mapping helpers for the analytics feature.
 * Static class per repo convention.
 */
export class AnalyticsUtils {
  /** "4h 12m" style watch-time label from milliseconds. */
  static formatWatchTime(ms: number): string {
    if (!isFinite(ms) || ms <= 0) return '0m';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  /** Byte label using the platform's binary (1024-based) convention. */
  static formatBytes(bytes: number): string {
    if (!isFinite(bytes) || bytes < 0) return '0 B';
    let value = bytes;
    let unitIndex = 0;
    while (value >= BYTES_PER_UNIT && unitIndex < BYTE_UNITS.length - 1) {
      value /= BYTES_PER_UNIT;
      unitIndex += 1;
    }
    const label = unitIndex === 0 ? String(Math.round(value)) : value.toFixed(1);
    return `${label} ${BYTE_UNITS[unitIndex]}`;
  }

  /** Locale-grouped integer count. */
  static formatCount(value: number): string {
    if (!isFinite(value)) return '0';
    return Math.round(value).toLocaleString();
  }

  /** A live snapshot built more than ~3 minutes ago is stale. */
  static isLiveStale(builtAtSeconds: number, nowSeconds: number): boolean {
    return nowSeconds - builtAtSeconds > ANALYTICS_LIVE_STALE_AFTER_SECONDS;
  }

  /**
   * Map a query error to a renderable view state — structured `code` first,
   * HTTP status second (plan error-handling table).
   */
  static errorView(error: unknown): AnalyticsErrorView {
    if (!(error instanceof ApiError)) {
      return { kind: 'generic', title: 'Analytics could not be loaded.' };
    }

    switch (error.code) {
      case 'range_outside_coverage':
        return {
          kind: 'empty',
          title: 'No analytics data for this range.',
          availableFrom: error.availableFrom,
          availableTo: error.availableTo,
        };
      case 'range_before_product_window':
        return {
          kind: 'empty',
          title: 'New Analytics starts later than this range.',
          detail: 'Pick a range on or after the analytics start date.',
          availableFrom: error.availableFrom,
        };
      case 'overloaded':
      case 'downstream_unavailable':
        return {
          kind: 'busy',
          title: 'Analytics is busy — retrying shortly.',
          detail: 'Try again in a moment.',
        };
      case 'query_timeout':
      case 'gateway_timeout':
        return {
          kind: 'timeout',
          title: 'This query took too long.',
          detail: 'Narrow the date range and retry.',
        };
      case 'range_too_large':
      case 'point_cap_exceeded':
      case 'invalid_time_range':
      case 'invalid_bucket':
      case 'invalid_dimension':
      case 'invalid_metric':
      case 'invalid_limit':
      case 'invalid_cid':
      case 'invalid_media_id':
        return {
          kind: 'rangeInvalid',
          title: 'This request could not be processed.',
          detail: 'Adjust the date range or selection and retry.',
        };
      case 'scope_inactive':
      case 'scope_not_found':
      case 'scope_removed':
      case 'not_top_level':
      case 'invalid_parent':
        return { kind: 'notAvailable', title: 'Not available for this account.' };
      default:
        break;
    }

    if (error.httpStatusCode === 403) {
      // Platform VError (not authenticated/authorized) — HTTP 403, no auto-logout.
      return { kind: 'notAuthorized', title: 'You are not authorized to view analytics.' };
    }
    if (error.httpStatusCode === 404) {
      // Gateway flag off (e.g. dev against the production proxy until enablement).
      return { kind: 'notEnabled', title: 'Analytics is not enabled in this environment.' };
    }
    return { kind: 'generic', title: 'Analytics could not be loaded.' };
  }
}
